import React, { useState, useEffect, useContext, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import {
    FaTrophy, FaCheckCircle, FaTimes, FaMedal, FaCrown,
    FaMap, FaCalendarAlt, FaUndo,
    FaQuestionCircle, FaBullhorn, FaFilePdf, FaDownload, FaUserNinja,
    FaRandom, FaArrowUp, FaArrowDown, FaCogs
} from 'react-icons/fa';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import { AppContext } from '../../context/AppContext';
import PDFTASheetPage from './PDFTASheetPage';

// --- HELPER FOTO ---
const getPhotoUrl = (imageFileName) => {
    if (!imageFileName) return null;
    const baseUrl = process.env.REACT_APP_BACKEND || '';
    return `${baseUrl}/api/uploads/proxy?file=uploads/athletes/${imageFileName}&cb=${new Date().getTime()}`;
};

// --- HELPER LAPANGAN ABJAD ---
const getArenaLetter = (arenaNum) => String.fromCharCode(64 + parseInt(arenaNum));

const normalizeText = (value) => String(value || '').toLowerCase().trim();

const isKyorugiPrestasiAthlete = (athlete) => {
    return (
        normalizeText(athlete?.competition_type) === 'kyorugi' &&
        normalizeText(athlete?.type) === 'prestasi'
    );
};

const isPrestasiMatch = (match) => {
    const isPoolMatch = match?.weight_class && (
        match.weight_class.includes('(Pool') ||
        match.weight_class.includes('Pool ')
    );

    return (
        normalizeText(match?.competition_type) === 'kyorugi' &&
        !isPoolMatch
    );
};

const isPlayableMatch = (match) => {
    return !!match && !match.is_bye && !!match.athlete_blue_id && !!match.athlete_red_id;
};

const isEmptyByeMatch = (match) => {
    return !!match && !!match.is_bye && !match.athlete_blue_id && !match.athlete_red_id && !match.winner_id;
};

const getTeamName = (athlete) => {
    return athlete?.team?.name || athlete?.Team?.name || '-';
};

const getAthleteName = (athlete) => {
    return athlete?.name || 'TBD';
};

const getMatchNumberText = (match) => match?.match_num || match?.match_no || '-';

const buildFeedersByNextId = (matches = []) => matches.reduce((acc, match) => {
    if (isEmptyByeMatch(match)) return acc;
    if (!match?.next_match_id) return acc;

    const key = String(match.next_match_id);
    if (!acc[key]) acc[key] = [];
    acc[key].push(match);
    acc[key].sort((a, b) => (parseInt(a.match_no || 0) - parseInt(b.match_no || 0)) || (parseInt(a.id || 0) - parseInt(b.id || 0)));

    return acc;
}, {});

const getSlotDisplay = (match, corner, feedersByNextId = {}) => {
    const isBlue = corner === 'blue';
    const athlete = isBlue ? match?.BlueAthlete : match?.RedAthlete;
    const athleteId = isBlue ? match?.athlete_blue_id : match?.athlete_red_id;

    if (athlete) {
        return {
            athlete,
            athleteId,
            name: getAthleteName(athlete),
            team: getTeamName(athlete),
            pending: false
        };
    }

    if (match?.is_bye && !isBlue) {
        return {
            athlete: null,
            athleteId: null,
            name: 'BYE',
            team: 'OTOMATIS LOLOS',
            pending: false
        };
    }

    const feeders = feedersByNextId[String(match?.id)] || [];
    const feeder = feeders[isBlue ? 0 : 1];

    if (feeder) {
        return {
            athlete: null,
            athleteId: null,
            name: `Pemenang ${getMatchNumberText(feeder)}`,
            team: 'Menunggu hasil',
            pending: true
        };
    }

    return {
        athlete: null,
        athleteId: null,
        name: 'TBD',
        team: 'Menunggu lawan',
        pending: true
    };
};

const normalizeClass = (value) => {
    return String(value || '').toLowerCase().replace(/\s+/g, '');
};

// ==========================================
// STYLING KHUSUS UNTUK ENGINE PDF (VEKTOR)
// ==========================================
const pdfStyles = StyleSheet.create({
    page: { padding: 30, backgroundColor: '#ffffff', fontFamily: 'Helvetica' },
    header: { textAlign: 'center', marginBottom: 15, borderBottom: '2pt solid #000', paddingBottom: 10 },
    title: { fontSize: 16, fontWeight: 'bold', textTransform: 'uppercase' },
    subtitle: { fontSize: 10, fontWeight: 'bold', marginTop: 4 },
    metaBox: { backgroundColor: '#f0f0f0', padding: 6, border: '1pt solid #ccc', textAlign: 'center', marginBottom: 15 },
    metaText: { fontSize: 9, fontWeight: 'bold', textTransform: 'uppercase' },
    table: { width: '100%', borderStyle: 'solid', borderWidth: 1, borderRightWidth: 0, borderBottomWidth: 0, marginTop: 10 },
    tableRow: { flexDirection: 'row' },
    tableHeader: { backgroundColor: '#e2e8f0', fontWeight: 'bold' },
    tableCol1: { width: '15%', borderStyle: 'solid', borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0, padding: 5 },
    tableCol2: { width: '35%', borderStyle: 'solid', borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0, padding: 5 },
    tableCol3: { width: '50%', borderStyle: 'solid', borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0, padding: 5 },
    tableCell: { fontSize: 9 }
});

const AdminKyorugiPrestasi = () => {
    const currentType = 'Prestasi';

    const { masterData, settings } = useContext(AppContext);
    const [selectedAthlete, setSelectedAthlete] = useState(null);
    const [activeTab, setActiveTab] = useState('merge');
    const [allAthletes, setAllAthletes] = useState([]);
    const [loading, setLoading] = useState(false);

    const [allMatches, setAllMatches] = useState([]);
    const [bracketMatches, setBracketMatches] = useState([]);
    const [poomsaeAthletes, setPoomsaeAthletes] = useState([]);
    const [isPoomsaeView, setIsPoomsaeView] = useState(false);

    const [staging, setStaging] = useState([]);
    const [viewFilter, setViewFilter] = useState({
        competition_type: 'Kyorugi', gender: 'Lelaki', division: 'Senior', weight_class: '', day: 1, arena: 1
    });
    const [schedFilter, setSchedFilter] = useState({ day: 1, arena: 1 });
    const [genOptions, setGenOptions] = useState({ day: 1, arena: 1, strategy: 'separate', match_format: 'alphabetic' });

    // STATE KHUSUS MERGE
    const [mergeClassA, setMergeClassA] = useState(null);
    const [mergeClassB, setMergeClassB] = useState(null);
    const [athleteA, setAthleteA] = useState(null);
    const [athleteB, setAthleteB] = useState(null);

    // STATE KHUSUS INJECT BYE
    const [injectModal, setInjectModal] = useState({ show: false, match: null, athleteId: '', matchNo: '', sequence: '' });

    const [champion, setChampion] = useState(null);
    const [showChampionModal, setShowChampionModal] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', action: null });
    const [printModal, setPrintModal] = useState({ show: false, type: '', eventName: settings?.event_name || 'KEJUARAAN TAEKWONDO' });
    const [editMatchModal, setEditMatchModal] = useState({
        show: false,
        match: null,
        matchNum: '',
        sequence: '',
        blueId: '',
        redId: ''
    });

    const [globalArenaLoads, setGlobalArenaLoads] = useState({});

    useEffect(() => {
        if (settings?.event_name) {
            setPrintModal(prev => ({
                ...prev,
                eventName: prev.eventName === 'KEJUARAAN TAEKWONDO'
                    ? settings.event_name
                    : prev.eventName
            }));
        }
    }, [settings?.event_name]);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const resAthletes = await api.get('/athletes/public-list');
            const rawAthletes = resAthletes.data.result || resAthletes.data || [];
            setAllAthletes(rawAthletes.filter(a => isKyorugiPrestasiAthlete(a)));

            const resAll = await api.get('/matches', {
                params: { competition_type: 'Kyorugi' }
            });
            const allData = resAll.data.bracket || resAll.data.result || resAll.data || [];

            const filteredMatches = allData.filter(m => isPrestasiMatch(m) && !isEmptyByeMatch(m));
            setAllMatches(filteredMatches);

            const totalLoads = {};
            filteredMatches.forEach(m => {
                if (isPlayableMatch(m)) {
                    const lKey = `${m.day}-${m.arena}`;
                    totalLoads[lKey] = (totalLoads[lKey] || 0) + 1;
                }
            });

            setGlobalArenaLoads(totalLoads);
        } catch (error) {
            console.error("🔴 Error Fetch Dashboard:", error);
            toast.error("Gagal memuat data Kyorugi Prestasi.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setStaging([]); fetchDashboardData();
        // eslint-disable-next-line
    }, [currentType]);

    const fetchBracketVisual = async () => {
        if (!viewFilter.weight_class) return;
        setLoading(true);
        try {
            const res = await api.get('/matches', { params: { ...viewFilter } });
            if (res.data.is_poomsae) {
                setIsPoomsaeView(true);
                setPoomsaeAthletes(res.data.data || []);
                setBracketMatches([]);
            } else {
                setIsPoomsaeView(false);
                const bracketData = res.data.bracket || res.data.result || res.data || [];
                setBracketMatches(bracketData.filter(m => !isEmptyByeMatch(m)));
                setPoomsaeAthletes([]);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // ==========================================
    // LOGIKA PENGURUTAN (SORTING) & PEMETAAN DATA
    // ==========================================
    const activeClasses = useMemo(() => {
        const classMap = {};

        allAthletes
            .filter(a => isKyorugiPrestasiAthlete(a))
            .forEach(a => {
                if (!a.weight_class) return;

                const key = `${a.competition_type}|${a.gender}|${a.division}|${a.weight_class}`;

                if (!classMap[key]) {
                    classMap[key] = {
                        competition_type: 'Kyorugi',
                        gender: a.gender,
                        division: a.division,
                        weight_class: a.weight_class,
                        athleteCount: 0,
                        key
                    };
                }

                classMap[key].athleteCount += 1;
            });

        const divisionOrder = {
            'pracadet c': 1,
            'pra-cadet c': 1,
            'pra cadet c': 1,
            'cadet': 2,
            'junior': 3,
            'senior': 4
        };

        const genderOrder = {
            'lelaki': 1,
            'perempuan': 2
        };

        const parseWeight = (wc) => {
            const str = String(wc || '').toUpperCase();
            const match = str.match(/([UO]|UNDER|OVER)\s*(\d+)/);

            if (match) {
                return {
                    weight: parseInt(match[2], 10),
                    isOver: match[1].startsWith('O')
                };
            }

            const numMatch = str.match(/(\d+)/);

            return numMatch
                ? { weight: parseInt(numMatch[1], 10), isOver: false }
                : { weight: 999, isOver: false };
        };

        return Object.values(classMap).sort((a, b) => {
            const divA = divisionOrder[normalizeText(a.division)] || 99;
            const divB = divisionOrder[normalizeText(b.division)] || 99;
            if (divA !== divB) return divA - divB;

            const genA = genderOrder[normalizeText(a.gender)] || 99;
            const genB = genderOrder[normalizeText(b.gender)] || 99;
            if (genA !== genB) return genA - genB;

            const wA = parseWeight(a.weight_class);
            const wB = parseWeight(b.weight_class);

            if (wA.weight !== wB.weight) return wA.weight - wB.weight;
            if (wA.isOver !== wB.isOver) return wA.isOver ? 1 : -1;

            return a.weight_class.localeCompare(b.weight_class, undefined, { numeric: true });
        });
    }, [allAthletes]);

    const activeClassesGenerate = useMemo(() => {
        return activeClasses.filter(c => c.athleteCount >= 2);
    }, [activeClasses]);
    const mergeKyorugiClasses = useMemo(() => activeClasses, [activeClasses]);

    const athletesClassA = useMemo(() => {
        return allAthletes.filter(a =>
            mergeClassA &&
            isKyorugiPrestasiAthlete(a) &&
            a.gender === mergeClassA.gender &&
            a.division === mergeClassA.division &&
            a.weight_class === mergeClassA.weight_class
        );
    }, [allAthletes, mergeClassA]);
    const athletesClassB = useMemo(() => {
        return allAthletes.filter(a =>
            mergeClassB &&
            isKyorugiPrestasiAthlete(a) &&
            a.gender === mergeClassB.gender &&
            a.division === mergeClassB.division &&
            a.weight_class === mergeClassB.weight_class
        );
    }, [allAthletes, mergeClassB]);

    const classAllocationsDB = useMemo(() => {
        const allocs = {};
        allMatches.forEach(m => {
            const key = `${m.competition_type}|${m.gender}|${m.division}|${m.weight_class}`.toLowerCase();
            allocs[key] = `H${m.day}-Lap ${getArenaLetter(m.arena)}`;
        });
        return allocs;
    }, [allMatches]);

    const computedLoads = useMemo(() => {
        const loads = { ...globalArenaLoads };
        staging.forEach(cls => {
            const lKey = `${cls.targetDay}-${cls.targetArena}`;
            const pendingMatches = cls.competition_type === 'Kyorugi' ? (cls.athleteCount - 1) : cls.athleteCount;
            loads[lKey] = (loads[lKey] || 0) + pendingMatches;
        });
        return loads;
    }, [globalArenaLoads, staging]);

    const availableClassesForDropdown = useMemo(() => {
        const targetAlloc = `H${viewFilter.day}-Lap ${getArenaLetter(viewFilter.arena)}`;
        return activeClassesGenerate.filter(c => {
            const key = `${c.competition_type}|${c.gender}|${c.division}|${c.weight_class}`.toLowerCase();
            return classAllocationsDB[key] === targetAlloc;
        }).sort((a, b) => b.athleteCount - a.athleteCount);
    }, [activeClassesGenerate, viewFilter.day, viewFilter.arena, classAllocationsDB]);

    // FILTER ATLET UNTUK INJECT KE BYE
    const availableAthletesForInject = useMemo(() => {
        if (!injectModal.show || !injectModal.match) return [];
        const m = injectModal.match;
        const pooledIds = new Set();
        allMatches.forEach(match => {
            if (match.athlete_blue_id) pooledIds.add(match.athlete_blue_id);
            if (match.athlete_red_id) pooledIds.add(match.athlete_red_id);
        });
        return allAthletes.filter(a =>
            isKyorugiPrestasiAthlete(a) &&
            a.gender === m.gender &&
            a.division === m.division &&
            normalizeClass(a.weight_class) === normalizeClass(m.weight_class) &&
            !pooledIds.has(a.id)
        );
    }, [allAthletes, allMatches, injectModal.show, injectModal.match]);

    const editableAthleteOptions = useMemo(() => {
        if (!editMatchModal.show || !editMatchModal.match) return [];

        const match = editMatchModal.match;

        return allAthletes
            .filter(a =>
                isKyorugiPrestasiAthlete(a) &&
                a.gender === match.gender &&
                a.division === match.division &&
                normalizeClass(a.weight_class) === normalizeClass(match.weight_class)
            )
            .sort((a, b) => getAthleteName(a).localeCompare(getAthleteName(b)));
    }, [allAthletes, editMatchModal.show, editMatchModal.match]);

    // ==========================================
    // AUTO-JUMP LOGIC
    // ==========================================
    useEffect(() => {
        if (activeTab === 'bracket' && !loading) {
            if (availableClassesForDropdown.length > 0) {
                const currentFilterMatches = availableClassesForDropdown.find(c =>
                    String(c.competition_type || '').toLowerCase() === String(viewFilter.competition_type || '').toLowerCase() &&
                    String(c.gender || '').toLowerCase() === String(viewFilter.gender || '').toLowerCase() &&
                    String(c.division || '').toLowerCase() === String(viewFilter.division || '').toLowerCase() &&
                    String(c.weight_class || '').toLowerCase() === String(viewFilter.weight_class || '').toLowerCase()
                );
                if (!currentFilterMatches) {
                    const firstValid = availableClassesForDropdown[0];
                    setViewFilter(prev => ({ ...prev, competition_type: firstValid.competition_type, gender: firstValid.gender, division: firstValid.division, weight_class: firstValid.weight_class }));
                }
            } else {
                if (allMatches.length > 0) {
                    const firstMatch = allMatches.find(m => m.competition_type === 'Kyorugi');
                    if (firstMatch && (firstMatch.day !== viewFilter.day || firstMatch.arena !== viewFilter.arena)) {
                        setViewFilter(prev => ({ ...prev, day: firstMatch.day, arena: firstMatch.arena, weight_class: '' }));
                        return;
                    }
                }
                if (viewFilter.weight_class !== '' && !loading) {
                    setViewFilter(prev => ({ ...prev, competition_type: '', gender: '', division: '', weight_class: '' }));
                    setBracketMatches([]);
                }
            }
        }
        // eslint-disable-next-line
    }, [viewFilter.day, viewFilter.arena, activeTab, availableClassesForDropdown, loading, allMatches]);

    useEffect(() => {
        if (activeTab === 'schedule' && !loading && allMatches.length > 0) {
            const currentHasMatches = allMatches.some(m => m.day === schedFilter.day && m.arena === schedFilter.arena && isPlayableMatch(m));
            if (!currentHasMatches) {
                const firstAvailableMatch = allMatches.find(m => isPlayableMatch(m));
                if (firstAvailableMatch) setSchedFilter({ day: firstAvailableMatch.day, arena: firstAvailableMatch.arena });
            }
        }
    }, [activeTab, loading, allMatches, schedFilter.day, schedFilter.arena]);

    useEffect(() => {
        if (activeTab === 'bracket' && viewFilter.weight_class) fetchBracketVisual();
        else if (activeTab === 'bracket' && !viewFilter.weight_class) setBracketMatches([]);
        // eslint-disable-next-line
    }, [viewFilter.weight_class, activeTab]);

    // ==========================================
    // FUNGSI AKSI: MERGE, QUEUE, CREATE, INJECT
    // ==========================================
    const handleInjectSubmit = async () => {
        if (!injectModal.athleteId) return toast.warning("Pilih atlet terlebih dahulu!");
        if (!injectModal.matchNo || !injectModal.sequence) return toast.warning("Nomor Partai dan Urutan harus diisi!");
        setLoading(true);
        try {
            const matchNumStr = isNaN(injectModal.matchNo) ? injectModal.matchNo : `${getArenaLetter(injectModal.match.arena)}${String(injectModal.matchNo).padStart(2, '0')}`;
            await api.put(`/matches/${injectModal.match.uuid}/inject-bye`, {
                athlete_id: injectModal.athleteId, match_no: parseInt(injectModal.matchNo), sequence: parseInt(injectModal.sequence), match_num: matchNumStr
            });
            toast.success("Atlet berhasil disisipkan. Bagan BYE menjadi partai aktif!");
            setInjectModal({ show: false, match: null, athleteId: '', matchNo: '', sequence: '' });
            fetchDashboardData();
            if (activeTab === 'bracket') fetchBracketVisual();
        } catch (error) {
            toast.error("Gagal menyisipkan atlet: " + (error.response?.data?.msg || "Internal Error"));
        } finally { setLoading(false); }
    };

    const executeMoveToB = async () => {
        if (!athleteA || !mergeClassB) return toast.warning("Pilih Atlet di Kotak Atas (A) dan Kelas Tujuan di Kotak Bawah (B)!");
        setConfirmModal({
            show: true, title: 'Konfirmasi Pindah', message: `Pindahkan ${getAthleteName(athleteA)} ke kelas ${mergeClassB.division} - ${mergeClassB.weight_class}?`,
            action: async () => {
                setConfirmModal({ show: false, title: '', message: '', action: null });
                setLoading(true);
                try {
                    await api.patch(`/athletes/move/${athleteA.uuid}`, { target_division: mergeClassB.division, target_weight_class: mergeClassB.weight_class });
                    toast.success("Atlet dipindahkan!"); setAthleteA(null); fetchDashboardData();
                } catch (error) { toast.error("Gagal memindahkan atlet"); } finally { setLoading(false); }
            }
        });
    };

    const executeMoveToA = async () => {
        if (!athleteB || !mergeClassA) return toast.warning("Pilih Atlet di Kotak Bawah (B) dan Kelas Tujuan di Kotak Atas (A)!");
        setConfirmModal({
            show: true, title: 'Konfirmasi Pindah', message: `Pindahkan ${getAthleteName(athleteB)} ke kelas ${mergeClassA.division} - ${mergeClassA.weight_class}?`,
            action: async () => {
                setConfirmModal({ show: false, title: '', message: '', action: null });
                setLoading(true);
                try {
                    await api.patch(`/athletes/move/${athleteB.uuid}`, { target_division: mergeClassA.division, target_weight_class: mergeClassA.weight_class });
                    toast.success("Atlet dipindahkan!"); setAthleteB(null); fetchDashboardData();
                } catch (error) { toast.error("Gagal memindahkan atlet"); } finally { setLoading(false); }
            }
        });
    };

    const handlePindahKeKanan = (cls) => {
        const newStaging = staging.filter(s => s.key !== cls.key);
        newStaging.push({ ...cls, targetDay: genOptions.day, targetArena: genOptions.arena });
        setStaging(newStaging);
    };

    const executeGenerateBatch = async () => {
        if (staging.length === 0) return toast.warning("Daftar antrean kosong.");
        setConfirmModal({
            show: true, title: 'Create Bagan?', message: `Membuat bagan pertandingan untuk ${staging.length} kelas sekaligus. Lanjutkan?`,
            action: async () => {
                setConfirmModal({ show: false, title: '', message: '', action: null });
                setLoading(true);
                let successCount = 0; const classToView = staging[0];
                for (const cls of staging) {
                    try {
                        await api.post('/matches/generate', { ...cls, day: cls.targetDay, arena: cls.targetArena, strategy: genOptions.strategy, match_format: genOptions.match_format, type: currentType });
                        successCount++;
                    } catch (error) { toast.error(`Gagal generate kelas ${cls.weight_class}`); }
                }
                if (successCount > 0) {
                    toast.success(`${successCount} kelas berhasil dibuat bagannya!`);
                    setViewFilter({ competition_type: classToView.competition_type, gender: classToView.gender, division: classToView.division, day: classToView.targetDay, arena: classToView.targetArena, weight_class: classToView.weight_class });
                    setActiveTab('bracket');
                }
                setStaging([]); fetchDashboardData();
            }
        });
    };

    const handleSetWinnerClick = (matchUuid, winnerId, colorText) => {
        setConfirmModal({
            show: true, title: 'Konfirmasi Kemenangan', message: `Tetapkan Sudut ${colorText} sebagai pemenang?`,
            action: async () => {
                setConfirmModal({ show: false, title: '', message: '', action: null });
                try {
                    await api.patch(`/matches/winner/${matchUuid}`, { winner_id: winnerId });
                    toast.success("Pemenang ditetapkan!"); fetchDashboardData();
                    if (activeTab === 'bracket') fetchBracketVisual();
                } catch (error) { toast.error("Gagal menetapkan pemenang."); }
            }
        });
    };

    const handleResetWinnerClick = (matchUuid) => {
        setConfirmModal({
            show: true, title: 'Batalkan Hasil', message: "Membatalkan pemenang mereset partai selanjutnya. Lanjut?",
            action: async () => {
                setConfirmModal({ show: false, title: '', message: '', action: null });
                try {
                    await api.patch(`/matches/reset/${matchUuid}`);
                    toast.success("Hasil dibatalkan."); fetchDashboardData();
                    if (activeTab === 'bracket') fetchBracketVisual();
                } catch (error) { toast.error("Gagal mereset."); }
            }
        });
    };

    const openEditMatchModal = (match) => {
        setEditMatchModal({
            show: true,
            match,
            matchNum: match.match_num || match.match_no || '',
            sequence: match.sequence || '',
            blueId: match.athlete_blue_id || '',
            redId: match.athlete_red_id || ''
        });
    };

    const handleEditMatchSubmit = async () => {
        const matchNum = String(editMatchModal.matchNum || '').trim();
        if (!matchNum) return toast.warning("Nama/nomor partai harus diisi.");
        const hadParticipant = !!(editMatchModal.match?.athlete_blue_id || editMatchModal.match?.athlete_red_id);
        if (hadParticipant && !editMatchModal.blueId && !editMatchModal.redId) return toast.warning("Minimal satu slot atlet harus terisi.");
        if (editMatchModal.blueId && editMatchModal.redId && matchNum.toUpperCase() === 'BYE') {
            return toast.warning("Partai aktif dengan dua atlet tidak boleh memakai nama BYE.");
        }

        setLoading(true);
        try {
            await api.patch(`/matches/${editMatchModal.match.uuid}/manual-edit`, {
                match_num: matchNum,
                sequence: editMatchModal.sequence,
                athlete_blue_id: editMatchModal.blueId || null,
                athlete_red_id: editMatchModal.redId || null
            });
            toast.success("Partai berhasil diperbarui.");
            setEditMatchModal({ show: false, match: null, matchNum: '', sequence: '', blueId: '', redId: '' });
            await fetchDashboardData();
            if (activeTab === 'bracket') fetchBracketVisual();
        } catch (error) {
            toast.error("Gagal mengubah partai: " + (error.response?.data?.msg || "Internal Error"));
        } finally {
            setLoading(false);
        }
    };

    const handleCallAthlete = (m) => {
        const matchNum = m.match_num || m.match_no;
        const blueName = getAthleteName(m.BlueAthlete);
        const blueTeam = getTeamName(m.BlueAthlete);

        const redName = getAthleteName(m.RedAthlete);
        const redTeam = getTeamName(m.RedAthlete);
        const lapAbjad = getArenaLetter(m.arena);
        const className = `${m.division || ''} ${m.weight_class || ''}`.trim();

        toast.info(`🔊 Panggilan Suara: Partai #${matchNum}`);
        if ('speechSynthesis' in window) {
            window.speechSynthesis.cancel();
            const textToSpeak = `Panggilan kepada peserta Kyorugi Prestasi, partai nomor, ${matchNum}, kelas ${className}. Sudut biru, ${blueName}, dari, ${blueTeam}. Melawan sudut merah, ${redName}, dari, ${redTeam}. Silakan segera menuju ke lapangan, ${lapAbjad}. Terima kasih.`;
            const utterance = new SpeechSynthesisUtterance(textToSpeak);
            utterance.lang = 'id-ID'; utterance.rate = 0.85;
            window.speechSynthesis.speak(utterance);
        } else toast.warning("Browser Anda tidak mendukung fitur panggilan suara.");
    };

    // ==========================================
    // PDF RENDERING
    // ==========================================
    const executeTASheetExport = async (match) => {
        const toastId = toast.loading("Membentuk TA Sheet PDF...");
        try {
            const eventName = settings?.event_name || 'KEJUARAAN TAEKWONDO';
            const MyPDFDocument = <Document><PDFTASheetPage eventName={eventName} match={match} variant="Kyorugi Prestasi" /></Document>;
            const blob = await pdf(MyPDFDocument).toBlob();
            saveAs(blob, `TA_Sheet_Partai_${match.match_num || match.match_no}.pdf`);
            toast.dismiss(toastId); toast.success("TA Sheet berhasil diunduh!");
        } catch (error) { toast.dismiss(toastId); toast.error("Gagal memproses TA Sheet PDF."); }
    };

    const executePDFExport = async () => {
        if (!printModal.eventName.trim()) return toast.warning("Nama Event harus diisi!");
        const toastId = toast.loading("Memproses PDF...");
        setPrintModal({ ...printModal, show: false });

        try {
            let MyPDFDocument;
            const groupInfo = { gender: viewFilter.gender, division: viewFilter.division, weight_class: viewFilter.weight_class };

            if (printModal.type === 'schedule') {
                const schedMatches = allMatches.filter(m => m.day === schedFilter.day && m.arena === schedFilter.arena && isPlayableMatch(m)).sort((a, b) => a.sequence - b.sequence);
                if (schedMatches.length === 0) {
                    toast.dismiss(toastId);
                    return toast.warning("Tidak ada jadwal Kyorugi Prestasi untuk hari dan lapangan ini.");
                }
                MyPDFDocument = <Document><PDFSchedulePage eventName={printModal.eventName} day={schedFilter.day} arena={schedFilter.arena} matches={schedMatches} /></Document>;
            } else if (printModal.type === 'batch') {
                const batchGroups = {};
                allMatches.forEach(m => {
                    if (m.day === viewFilter.day && m.arena === viewFilter.arena) {
                        const key = `${m.division}|${m.weight_class}|${m.gender}`;
                        if (!batchGroups[key]) batchGroups[key] = { matches: [], info: { division: m.division, weight_class: m.weight_class, gender: m.gender, day: m.day, arena: m.arena } };
                        batchGroups[key].matches.push(m);
                    }
                });
                if (Object.keys(batchGroups).length === 0) {
                    toast.dismiss(toastId);
                    return toast.warning("Tidak ada bagan Kyorugi Prestasi untuk hari dan lapangan ini.");
                }
                MyPDFDocument = (
                    <Document>
                        {Object.values(batchGroups).map((g, idx) => (
                            <PDFBracketPage key={idx} eventName={printModal.eventName} groupInfo={g.info} matches={g.matches} day={g.info.day} arena={g.info.arena} />
                        ))}
                    </Document>
                );
            } else {
                if (isPoomsaeView) MyPDFDocument = <Document><PDFPoomsaePage eventName={printModal.eventName} groupInfo={groupInfo} athletes={poomsaeAthletes} /></Document>;
                else {
                    if (bracketMatches.length === 0) {
                        toast.dismiss(toastId);
                        return toast.warning("Pilih kelas yang sudah memiliki bagan terlebih dahulu.");
                    }
                    MyPDFDocument = <Document><PDFBracketPage eventName={printModal.eventName} groupInfo={groupInfo} matches={bracketMatches} medalists={medalists} day={viewFilter.day} arena={viewFilter.arena} /></Document>;
                }
            }

            const blob = await pdf(MyPDFDocument).toBlob();
            const fileName = printModal.type === 'batch' ? `Bagan_Massal_H${viewFilter.day}_Lap${getArenaLetter(viewFilter.arena)}.pdf` : printModal.type === 'schedule' ? `Jadwal_H${schedFilter.day}_Lap${getArenaLetter(schedFilter.arena)}.pdf` : `Bagan_${viewFilter.weight_class || 'Kelas'}.pdf`;
            saveAs(blob, fileName);

            toast.update(toastId, { render: "Dokumen diunduh!", type: "success", isLoading: false, autoClose: 3000 });
        } catch (error) {
            console.error(error);
            toast.update(toastId, { render: "Gagal merakit PDF.", type: "error", isLoading: false, autoClose: 4000 });
        }
    };

    // KOMPONEN PDF
    const PDFBracketContent = ({ eventName, groupInfo, matches, medalists, day, arena }) => {
        const uniqueAthletes = new Set();
        matches.forEach(m => {
            if (m.athlete_blue_id) uniqueAthletes.add(m.athlete_blue_id);
            if (m.athlete_red_id) uniqueAthletes.add(m.athlete_red_id);
        });

        const bGrouped = matches.reduce((acc, m) => {
            const round = parseInt(m.round_no);
            if (!acc[round]) acc[round] = [];
            acc[round].push(m);
            return acc;
        }, {});
        const bKeys = Object.keys(bGrouped).map(Number).sort((a, b) => a - b);
        const feedersByNextId = buildFeedersByNextId(matches);

        let totalR = 0;
        let maxMatchesInColumn = 0;

        if (matches.length > 0) {
            maxMatchesInColumn = Math.max(...Object.values(bGrouped).map(arr => arr.length));
            const r1 = matches.filter(m => parseInt(m.round_no) === 1).length;
            if (r1 > 0) {
                let power = 1;
                while (power < r1) power *= 2;
                totalR = Math.log2(power) + 1;
            } else {
                totalR = bKeys.length > 0 ? parseInt(bKeys[bKeys.length - 1]) : 0;
            }
        }

        const isSuperDense = maxMatchesInColumn >= 16;
        const isDense = maxMatchesInColumn >= 8 && maxMatchesInColumn < 16;

        const ui = {
            paddingRow: isSuperDense ? '1 3' : (isDense ? '2 4' : '2 5'),
            nameSize: isSuperDense ? 6 : (isDense ? 7.2 : 8),
            teamSize: isSuperDense ? 5 : (isDense ? 5.6 : 5.8),
            badgeSize: isSuperDense ? 4 : (isDense ? 4.5 : 5),
            matchNumSize: isSuperDense ? 5 : (isDense ? 6 : 7),
            boxRadius: isSuperDense ? 1 : (isDense ? 2 : 3),
            paddingVertical: isSuperDense ? 0.5 : (isDense ? 1 : 2),
            paddingRightCol: isSuperDense ? 15 : (isDense ? 20 : 25),
            paddingRightFinal: isSuperDense ? 25 : (isDense ? 30 : 40),
            connectorWidth: isSuperDense ? 7.5 : (isDense ? 10 : 12.5),
            finalLineWidth: isSuperDense ? 25 : (isDense ? 30 : 40),
            boxHeight: isSuperDense ? 38 : (isDense ? 42 : 50),
            textLineHeight: isSuperDense ? 1.05 : 1.08
        };

        const WinLoseBadge = ({ athleteId, winnerId }) => {
            if (!winnerId || !athleteId) return null;
            const isWinner = athleteId === winnerId;

            return (
                <View style={{
                    backgroundColor: isWinner ? '#16a34a' : '#dc2626',
                    borderRadius: ui.boxRadius,
                    padding: isSuperDense ? '1 2' : '2 3',
                    marginRight: isSuperDense ? 3 : 5,
                    alignSelf: 'center',
                }}>
                    <Text style={{ fontSize: ui.badgeSize, fontWeight: 'bold', color: '#ffffff' }}>
                        {isWinner ? 'WIN' : 'LOSE'}
                    </Text>
                </View>
            );
        };

        const renderMatchBox = (m) => {
            const blueSlot = getSlotDisplay(m, 'blue', feedersByNextId);
            const redSlot = getSlotDisplay(m, 'red', feedersByNextId);

            return (
                <View style={{ flexDirection: 'row', alignItems: 'stretch', border: '1pt solid #000', borderRadius: ui.boxRadius, backgroundColor: '#fff', opacity: m.is_bye ? 0.6 : 1, zIndex: 2, height: ui.boxHeight }}>
                    <View style={{ flex: 1 }}>
                        <View style={{ flexDirection: 'row', padding: ui.paddingRow, borderBottom: '0.5pt solid #000', backgroundColor: '#eff6ff', flex: 1 }}>
                            <WinLoseBadge athleteId={m.athlete_blue_id} winnerId={m.winner_id} />
                            <View style={{ flex: 1, justifyContent: 'center' }}>
                                <Text style={{ fontSize: ui.nameSize, fontWeight: 'bold', lineHeight: ui.textLineHeight, marginBottom: 0.5 }}>
                                    {blueSlot.name}
                                </Text>
                                <Text style={{ fontSize: ui.teamSize, lineHeight: ui.textLineHeight, color: blueSlot.pending ? '#94a3b8' : '#64748b' }}>
                                    {blueSlot.team}
                                </Text>
                            </View>
                        </View>

                        <View style={{ flexDirection: 'row', padding: ui.paddingRow, backgroundColor: '#fff', flex: 1 }}>
                            <WinLoseBadge athleteId={m.athlete_red_id} winnerId={m.winner_id} />
                            <View style={{ flex: 1, justifyContent: 'center' }}>
                                <Text style={{ fontSize: ui.nameSize, fontWeight: 'bold', lineHeight: ui.textLineHeight, marginBottom: 0.5 }}>
                                    {redSlot.name}
                                </Text>
                                <Text style={{ fontSize: ui.teamSize, lineHeight: ui.textLineHeight, color: redSlot.pending ? '#94a3b8' : '#64748b' }}>
                                    {redSlot.team}
                                </Text>
                            </View>
                        </View>
                    </View>
                    <View style={{ width: 20, backgroundColor: '#e2e8f0', borderLeft: '1pt solid #000', justifyContent: 'center', alignItems: 'center' }}>
                        <Text style={{ fontSize: ui.matchNumSize, fontWeight: 'bold' }}>{m.is_bye ? 'BYE' : (m.match_num || m.match_no)}</Text>
                    </View>
                </View>
            );
        };

        return (
            <View wrap={false} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <View style={{ textAlign: 'center', marginBottom: 5, borderBottom: '1.5pt solid #000', paddingBottom: 3 }}>
                    <Text style={{ fontSize: 16, fontWeight: 'bold', textTransform: 'uppercase' }}>{eventName}</Text>
                    <Text style={{ fontSize: 10, fontWeight: 'bold', marginTop: 2 }}>OFFICIAL BOUT CHART - KYORUGI PRESTASI</Text>
                </View>

                <View style={{ backgroundColor: '#f1f5f9', padding: 4, border: '0.5pt solid #000', textAlign: 'center', marginBottom: 8 }}>
                    <Text style={{ fontSize: 9, fontWeight: 'bold', textTransform: 'uppercase' }}>
                        HARI {day} - LAPANGAN {getArenaLetter(arena)} | {groupInfo.division} | {groupInfo.weight_class} | {groupInfo.gender === 'Lelaki' ? 'PUTRA' : 'PUTRI'} | {uniqueAthletes.size} PESERTA
                    </Text>
                </View>

                <View style={{ flexDirection: 'row', width: '100%', marginBottom: 5, justifyContent: 'flex-start' }}>
                    {bKeys.map((round, rIdx) => {
                        const isFinalRound = rIdx === bKeys.length - 1;
                        return (
                            <View key={`h-${round}`} style={{ flex: 1, maxWidth: 220, textAlign: 'center', paddingRight: isFinalRound ? ui.paddingRightFinal : ui.paddingRightCol }}>
                                <View style={{ backgroundColor: '#334155', padding: 4, borderRadius: 2 }}>
                                    <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#ffffff' }}>
                                        {getRoundName(round, totalR).toUpperCase()}
                                    </Text>
                                </View>
                            </View>
                        );
                    })}
                </View>

                <View style={{ flexDirection: 'row', width: '100%', flex: 1, justifyContent: 'flex-start' }}>
                    {bKeys.map((round, rIdx) => {
                        const isFinalRound = rIdx === bKeys.length - 1;
                        const matchesInRound = bGrouped[round];
                        const pairs = [];
                        for (let i = 0; i < matchesInRound.length; i += 2) {
                            pairs.push([matchesInRound[i], matchesInRound[i + 1]]);
                        }

                        return (
                            <View key={round} style={{ flex: 1, maxWidth: 220, flexDirection: 'column' }}>
                                {pairs.map((pair, pIdx) => {
                                    const m1 = pair[0];
                                    const m2 = pair[1];

                                    return (
                                        <View key={pIdx} style={{ flex: 1, position: 'relative', flexDirection: 'column', justifyContent: 'center' }}>
                                            {m2 ? (
                                                <>
                                                    <View style={{ flex: 1, justifyContent: 'center', paddingRight: isFinalRound ? ui.paddingRightFinal : ui.paddingRightCol, paddingVertical: ui.paddingVertical }}>{renderMatchBox(m1)}</View>
                                                    <View style={{ flex: 1, justifyContent: 'center', paddingRight: isFinalRound ? ui.paddingRightFinal : ui.paddingRightCol, paddingVertical: ui.paddingVertical }}>{renderMatchBox(m2)}</View>
                                                </>
                                            ) : (
                                                <View style={{ flex: 1, justifyContent: 'center', paddingRight: isFinalRound ? ui.paddingRightFinal : ui.paddingRightCol, paddingVertical: ui.paddingVertical }}>{renderMatchBox(m1)}</View>
                                            )}
                                            {!isFinalRound && m2 && (
                                                <View style={{ position: 'absolute', right: ui.connectorWidth, top: '25%', bottom: '25%', width: ui.connectorWidth, borderTop: '1.5pt solid #000', borderBottom: '1.5pt solid #000', borderRight: '1.5pt solid #000', zIndex: 1 }} />
                                            )}
                                            {!isFinalRound && (
                                                <View style={{ position: 'absolute', right: 0, top: '50%', width: m2 ? ui.connectorWidth : (ui.connectorWidth * 2), borderBottom: '1.5pt solid #000', zIndex: 1 }} />
                                            )}
                                            {isFinalRound && medalists?.gold && (
                                                <View style={{ position: 'absolute', right: 0, top: '50%', width: ui.finalLineWidth, borderBottom: '2pt solid #b8860b', zIndex: 1 }}>
                                                    <Text style={{ position: 'absolute', bottom: 3, left: 4, fontSize: isSuperDense ? 6 : 8, fontWeight: 'bold', color: '#b8860b' }}>
                                                        WIN: {getAthleteName(medalists.gold).split(' ').slice(0, 2).join(' ')}
                                                    </Text>
                                                </View>
                                            )}
                                        </View>
                                    );
                                })}
                            </View>
                        );
                    })}
                </View>

                {medalists && (medalists.gold || medalists.silver || medalists.bronze1) && (
                    <View style={{ marginTop: 10, paddingTop: 6, borderTop: '1.5pt solid #000', flexDirection: 'row', justifyContent: 'space-between' }} wrap={false}>
                        {[
                            { label: 'JUARA 1 (EMAS)', data: medalists.gold, color: '#854d0e' },
                            { label: 'JUARA 2 (PERAK)', data: medalists.silver, color: '#475569' },
                            { label: 'JUARA 3 (PERUNGGU)', data: medalists.bronze1, color: '#9a3412' },
                            { label: 'JUARA 3 (PERUNGGU)', data: medalists.bronze2, color: '#9a3412' }
                        ].map((juara, idx) => (
                            <View key={idx} style={{ textAlign: 'center', width: '24%', border: '1pt solid #cbd5e1', padding: 4, borderRadius: 3 }}>
                                <Text style={{ fontSize: 8, fontWeight: 'bold', color: juara.color, marginBottom: 2 }}>{juara.label}</Text>
                                <Text style={{ fontSize: 10, fontWeight: 'bold' }}>{juara.data ? getAthleteName(juara.data) : '---'}</Text>
                                <Text style={{ fontSize: 8, color: '#64748b', marginTop: 1 }}>{juara.data ? getTeamName(juara.data) : '-'}</Text>
                            </View>
                        ))}
                    </View>
                )}
            </View>
        );
    };

    const PDFBracketPage = (props) => (
        <Page size="A4" orientation="landscape" style={{ padding: 15, backgroundColor: '#ffffff', fontFamily: 'Helvetica' }}>
            <PDFBracketContent {...props} />
        </Page>
    );

    const PDFSchedulePage = ({ matches, eventName, day, arena }) => (
        <Page size="A4" orientation="portrait" style={pdfStyles.page}>
            <View style={pdfStyles.header}>
                <Text style={pdfStyles.title}>{eventName}</Text>
                <Text style={pdfStyles.subtitle}>JADWAL PEMANGGILAN KYORUGI PRESTASI (HARI {day} - LAPANGAN {getArenaLetter(arena)})</Text>
            </View>
            <View style={pdfStyles.table}>
                <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
                    <View style={pdfStyles.tableCol1}><Text style={pdfStyles.tableCell}>Partai</Text></View>
                    <View style={pdfStyles.tableCol2}><Text style={pdfStyles.tableCell}>Kategori / Kelas</Text></View>
                    <View style={pdfStyles.tableCol3}><Text style={pdfStyles.tableCell}>Pertandingan (Merah vs Biru)</Text></View>
                </View>
                {matches.map((m, i) => (
                    <View style={pdfStyles.tableRow} key={i}>
                        <View style={pdfStyles.tableCol1}><Text style={[pdfStyles.tableCell, { fontWeight: 'bold' }]}>#{m.match_num || m.match_no}</Text></View>
                        <View style={pdfStyles.tableCol2}><Text style={pdfStyles.tableCell}>{m.division} - {m.weight_class}</Text></View>
                        <View style={pdfStyles.tableCol3}><Text style={pdfStyles.tableCell}>{getAthleteName(m.RedAthlete)} vs {getAthleteName(m.BlueAthlete)}</Text></View>
                    </View>
                ))}
            </View>
        </Page>
    );

    const PDFPoomsaePage = ({ eventName, groupInfo, athletes }) => (
        <Page size="A4" style={pdfStyles.page}><Text style={pdfStyles.title}>{eventName}</Text><Text>POOMSAE: {groupInfo.weight_class}</Text></Page>
    );

    // ==========================================
    // RENDER HELPER & UI
    // ==========================================
    const getRoundName = (roundNo, totalRounds) => {
        const r = parseInt(roundNo); const t = parseInt(totalRounds);
        if (r === t) return "Final";
        if (r === t - 1) return "Semifinal";
        if (r === t - 2) return "Quarter Final";
        return `Qualification ${r}`;
    };

    const groupedMatches = bracketMatches.reduce((acc, m) => {
        if (!acc[m.round_no]) acc[m.round_no] = [];
        acc[m.round_no].push(m); return acc;
    }, {});
    const roundKeys = Object.keys(groupedMatches).sort((a, b) => a - b);
    let totalRounds = 0;
    if (bracketMatches.length > 0) {
        const r1Count = bracketMatches.filter(m => parseInt(m.round_no) === 1).length;
        if (r1Count > 0) { let p = 1; while (p < r1Count) p *= 2; totalRounds = Math.log2(p) + 1; }
        else totalRounds = roundKeys.length > 0 ? parseInt(roundKeys[roundKeys.length - 1]) : 0;
    }

    const medalists = useMemo(() => {
        if (bracketMatches.length === 0 || totalRounds === 0) return null;
        const finalMatch = bracketMatches.find(m => parseInt(m.round_no) === totalRounds);
        const semiMatches = bracketMatches.filter(m => parseInt(m.round_no) === totalRounds - 1);
        let gold = null, silver = null, bronze1 = null, bronze2 = null;
        if (finalMatch?.winner_id) {
            gold = finalMatch.winner_id === finalMatch.athlete_blue_id ? finalMatch.BlueAthlete : finalMatch.RedAthlete;
            silver = finalMatch.winner_id === finalMatch.athlete_blue_id ? finalMatch.RedAthlete : finalMatch.BlueAthlete;
        }
        if (semiMatches.length > 0) {
            bronze1 = semiMatches[0]?.winner_id === semiMatches[0]?.athlete_blue_id ? semiMatches[0]?.RedAthlete : semiMatches[0]?.BlueAthlete;
            bronze2 = semiMatches[1]?.winner_id === semiMatches[1]?.athlete_blue_id ? semiMatches[1]?.RedAthlete : semiMatches[1]?.BlueAthlete;
        }
        return { gold, silver, bronze1, bronze2 };
    }, [bracketMatches, totalRounds]);

    useEffect(() => {
        if (activeTab === 'bracket' && !isPoomsaeView && medalists?.gold && champion?.id !== medalists.gold.id) {
            setChampion(medalists.gold); setTimeout(() => setShowChampionModal(true), 500);
        } else if (!medalists?.gold) setChampion(null);
    }, [medalists, activeTab, champion, isPoomsaeView]);

    const BracketGridRenderer = ({ grouped, keys, totalR }) => {
        const renderMatches = Object.values(grouped).flat();
        const feedersByNextId = buildFeedersByNextId(renderMatches);

        return (
            <div className="d-flex bracket-wrapper pb-4">
                {keys.map((round, rIdx) => (
                    <div key={round} className="bracket-column d-flex flex-column justify-content-around position-relative" style={{ minWidth: '280px', padding: '0 25px' }}>
                    <div className="text-center position-absolute w-100" style={{ top: '-25px', left: 0 }}>
                        <span className="badge bg-secondary bg-opacity-25 text-dark rounded-pill px-3 py-1 border shadow-sm fw-bold">{getRoundName(round, totalR).toUpperCase()}</span>
                    </div>
                    {grouped[round].map((m) => {
                        const isBlueWin = m.winner_id && m.winner_id === m.athlete_blue_id;
                        const isRedWin = m.winner_id && m.winner_id === m.athlete_red_id;
                        const blueSlot = getSlotDisplay(m, 'blue', feedersByNextId);
                        const redSlot = getSlotDisplay(m, 'red', feedersByNextId);

                        return (
                            <div key={m.id} className="match-card-wrapper position-relative my-3">
                                {rIdx < keys.length - 1 && <div className="connector-line-right"></div>}
                                {rIdx > 0 && <div className="connector-line-left"></div>}

                                {m.is_bye ? (
                                    <div className="card border-0 match-card overflow-hidden bg-light position-relative" style={{ borderRadius: '16px', border: '1px dashed #ccc' }}>
                                        <button
                                            className="btn btn-sm btn-warning position-absolute top-0 start-0 m-1 border shadow-sm rounded-pill d-flex justify-content-center align-items-center gap-1 fw-bold px-2 py-1 hover-scale no-print"
                                            style={{ zIndex: 20 }}
                                            title="Edit Partai"
                                            onClick={() => openEditMatchModal(m)}
                                        >
                                            <FaCogs size={10} />
                                            <span className="match-edit-label">Edit</span>
                                        </button>
                                        <div className="p-2 px-5 d-flex align-items-center opacity-75">
                                            <div className="rounded-circle me-2 border border-2 border-secondary bg-white d-flex justify-content-center align-items-center text-muted" style={{ width: '30px', height: '30px' }}><FaUserNinja size={10} /></div>
                                            <div className="flex-grow-1 text-truncate" style={{ minWidth: 0 }}>
                                                <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.65rem' }}>{getAthleteName(m.BlueAthlete)}</div>
                                                <div className="text-muted fw-bold text-uppercase" style={{ fontSize: '0.5rem' }}>BYE (OTOMATIS LOLOS)</div>
                                            </div>
                                        </div>
                                        <button
                                            className="btn btn-sm btn-primary position-absolute top-0 end-0 m-1 border shadow-sm rounded-pill d-flex justify-content-center align-items-center gap-1 fw-bold px-2 py-1 hover-scale no-print"
                                            style={{ zIndex: 20 }}
                                            title="Sisipkan Atlet Baru"
                                            onClick={() => setInjectModal({ show: true, match: m, athleteId: '', matchNo: '', sequence: '' })}
                                        >
                                            <FaCogs size={10} />
                                            <span className="match-edit-label">Sisip</span>
                                        </button>
                                    </div>
                                ) : (
                                    <div className="card border-0 shadow-sm match-card overflow-hidden" style={{ borderRadius: '16px', border: '1px solid #ccc' }}>
                                        <div className="bg-light px-3 py-2 d-flex justify-content-between align-items-center gap-2 border-bottom">
                                            <span className="badge bg-white text-dark border px-2 py-1 shadow-sm text-truncate" style={{ fontSize: '0.65rem', minWidth: 0 }}>H{m.day}-Lap {getArenaLetter(m.arena)}</span>
                                            <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                                <span className="fw-black text-primary text-nowrap" style={{ fontSize: '0.75rem' }}>Partai #{m.match_num || m.match_no}</span>
                                                <button className="btn btn-sm btn-warning border rounded-pill d-flex align-items-center justify-content-center gap-1 fw-bold px-2 py-1 no-print flex-shrink-0" title="Edit Partai" onClick={() => openEditMatchModal(m)}>
                                                    <FaCogs size={10} />
                                                    <span className="match-edit-label">Edit</span>
                                                </button>
                                            </div>
                                        </div>
                                        <div className={`p-2 border-bottom d-flex align-items-center cursor-pointer hover-bg-light ${isBlueWin ? 'bg-primary bg-opacity-10' : 'bg-white'}`} onClick={() => blueSlot.athlete && setSelectedAthlete(blueSlot.athlete)}>
                                            {m.BlueAthlete?.image ? (
                                                <img crossOrigin="anonymous" src={getPhotoUrl(m.BlueAthlete.image)} alt="" className="rounded-circle me-2 border border-2 border-primary" style={{ width: '35px', height: '35px', objectFit: 'cover', flexShrink: 0 }} />
                                            ) : (
                                                <div className="rounded-circle me-2 border border-2 border-primary bg-light d-flex justify-content-center align-items-center text-muted" style={{ width: '35px', height: '35px', flexShrink: 0 }}><FaUserNinja /></div>
                                            )}
                                            <div className="flex-grow-1 text-truncate" style={{ minWidth: 0 }}>
                                                <div className={`fw-black text-truncate ${isBlueWin ? 'text-primary' : blueSlot.pending ? 'text-secondary' : 'text-dark'}`} style={{ fontSize: '0.75rem' }}>{blueSlot.name}</div>
                                                <div className="text-muted fw-bold text-uppercase" style={{ fontSize: '0.55rem' }}>{blueSlot.team}</div>
                                            </div>
                                            {isBlueWin && <FaCheckCircle className="text-primary ms-1" size={14} />}
                                        </div>
                                        <div className={`p-2 d-flex align-items-center cursor-pointer hover-bg-light ${isRedWin ? 'bg-danger bg-opacity-10' : 'bg-white'}`} onClick={() => redSlot.athlete && setSelectedAthlete(redSlot.athlete)}>
                                            {m.RedAthlete?.image ? (
                                                <img crossOrigin="anonymous" src={getPhotoUrl(m.RedAthlete.image)} alt="" className="rounded-circle me-2 border border-2 border-danger" style={{ width: '35px', height: '35px', objectFit: 'cover', flexShrink: 0 }} />
                                            ) : (
                                                <div className="rounded-circle me-2 border border-2 border-danger bg-light d-flex justify-content-center align-items-center text-muted" style={{ width: '35px', height: '35px', flexShrink: 0 }}><FaUserNinja /></div>
                                            )}
                                            <div className="flex-grow-1 text-truncate" style={{ minWidth: 0 }}>
                                                <div className={`fw-black text-truncate ${isRedWin ? 'text-danger' : redSlot.pending ? 'text-secondary' : 'text-dark'}`} style={{ fontSize: '0.75rem' }}>{redSlot.name}</div>
                                                <div className="text-muted fw-bold text-uppercase" style={{ fontSize: '0.55rem' }}>{redSlot.team}</div>
                                            </div>
                                            {isRedWin && <FaCheckCircle className="text-danger ms-1" size={14} />}
                                        </div>
                                    </div>
                                )}
                                {!m.is_bye && (
                                    <div className="text-center mt-2 no-print" data-html2canvas-ignore="true">
                                        {m.BlueAthlete && m.RedAthlete && !m.winner_id && (
                                            <div className="d-flex justify-content-center gap-1">
                                                <button className="btn btn-sm btn-primary fw-bold px-2 py-1" style={{ fontSize: '0.65rem' }} onClick={() => handleSetWinnerClick(m.uuid, m.athlete_blue_id, 'Biru')}>Biru</button>
                                                <button className="btn btn-sm btn-danger fw-bold px-2 py-1" style={{ fontSize: '0.65rem' }} onClick={() => handleSetWinnerClick(m.uuid, m.athlete_red_id, 'Merah')}>Merah</button>
                                            </div>
                                        )}
                                        {m.winner_id && <button className="btn btn-sm btn-light border text-danger fw-bold" style={{ fontSize: '0.65rem' }} onClick={() => handleResetWinnerClick(m.uuid)}><FaUndo className="me-1" /> Batal</button>}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    </div>
                ))}
            </div>
        );
    };

    const maxDays = masterData?.days || 1;
    const arenasPerDay = masterData?.arenasPerDay || [3];
    const scheduleMatchesToRender = allMatches.filter(m => m.day === schedFilter.day && m.arena === schedFilter.arena && isPlayableMatch(m)).sort((a, b) => a.sequence - b.sequence);

    const renderScheduleParticipant = (match, corner) => {
        const isBlue = corner === 'blue';
        const athlete = isBlue ? match.BlueAthlete : match.RedAthlete;
        const athleteId = isBlue ? match.athlete_blue_id : match.athlete_red_id;
        const cornerText = isBlue ? 'Biru' : 'Merah';
        const borderClass = isBlue ? 'border-primary' : 'border-danger';
        const buttonClass = isBlue ? 'btn-primary' : 'btn-danger';

        const isWinner = match.winner_id && match.winner_id === athleteId;

        return (
            <div className={`prestasi-participant-row p-3 border-start border-4 ${borderClass} ${isBlue ? 'border-bottom' : ''}`}>
                <div className="prestasi-participant-info">
                    <div className="fw-bold text-dark text-truncate">
                        {getAthleteName(athlete)}
                    </div>
                    <div className="small text-muted text-truncate">
                        {getTeamName(athlete)}
                    </div>
                    <div className="small text-muted">
                        {match.division || '-'} • {match.weight_class || '-'}
                    </div>
                </div>

                <div className="prestasi-action-area">
                    {isWinner ? (
                        <span className={`badge ${isBlue ? 'bg-primary' : 'bg-danger'} rounded-pill px-3 py-2`}>
                            Menang
                        </span>
                    ) : (
                        !match.winner_id && athleteId && (
                            <button
                                className={`btn btn-sm ${buttonClass} fw-bold rounded-pill prestasi-set-btn`}
                                onClick={() => handleSetWinnerClick(match.uuid, athleteId, cornerText)}
                            >
                                Set Menang
                            </button>
                        )
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="container-fluid p-0 d-flex flex-column h-100 position-relative animate-fade-in" style={{ backgroundColor: '#f4f7fe' }}>
            {/* Navigasi Utama */}
            <div className="mb-4 bg-white rounded-pill shadow-sm p-2 border d-flex gap-2 w-100 overflow-auto" style={{ maxWidth: 'fit-content' }}>
                <button className={`btn rounded-pill px-4 py-2 fw-bold flex-fill text-nowrap transition-all ${activeTab === 'merge' ? 'btn-primary' : 'btn-white text-muted hover-bg-light'}`} onClick={() => setActiveTab('merge')}>
                    <FaRandom className="me-2" /> Pindah Kelas (Merge)
                </button>
                <button className={`btn rounded-pill px-4 py-2 fw-bold flex-fill text-nowrap transition-all ${activeTab === 'generate' ? 'btn-primary' : 'btn-white text-muted hover-bg-light'}`} onClick={() => { setActiveTab('generate'); fetchDashboardData(); }}>
                    <FaMap className="me-2" /> Distribusi & Setting
                </button>
                <button className={`btn rounded-pill px-4 py-2 fw-bold flex-fill text-nowrap transition-all ${activeTab === 'bracket' ? 'btn-primary' : 'btn-white text-muted hover-bg-light'}`} onClick={() => setActiveTab('bracket')}>
                    <FaTrophy className="me-2" /> Bagan & Kyorugi
                </button>
                <button className={`btn rounded-pill px-4 py-2 fw-bold flex-fill text-nowrap transition-all ${activeTab === 'schedule' ? 'btn-primary' : 'btn-white text-muted hover-bg-light'}`} onClick={() => { setActiveTab('schedule'); fetchDashboardData(); }}>
                    <FaCalendarAlt className="me-2" /> Jadwal & Hasil
                </button>
            </div>

            {/* TAB 0: MERGE */}
            {activeTab === 'merge' && (
                <div className="d-flex flex-column gap-3 pb-5">
                    <div className="alert alert-warning border-0 shadow-sm rounded-4 d-flex align-items-center gap-2 gap-md-3 py-2 py-md-3 px-3">
                        <FaRandom size={20} className="text-warning flex-shrink-0" />
                        <div>
                            <strong className="d-block text-dark" style={{ fontSize: '0.9rem' }}>Merge Kelas Atlet</strong>
                            <span className="text-dark d-none d-md-inline" style={{ fontSize: '0.8rem' }}>Pilih atlet yang sendirian di kelasnya (ditandai merah), lalu pilih kelas tujuan, dan tekan panah di tengah untuk memindahkan.</span>
                            <span className="text-dark d-inline d-md-none" style={{ fontSize: '0.75rem' }}>Pilih atlet (merah), pilih tujuan, lalu klik panah pindah.</span>
                        </div>
                    </div>

                    <div className="row g-2 g-md-3">
                        <div className="col-6">
                            <div className="card shadow-sm border-0 rounded-3 rounded-md-4 h-100 bg-white">
                                <div className="card-header bg-light p-2 p-md-3 border-bottom"><h6 className="m-0 fw-bold text-dark text-truncate" style={{ fontSize: '0.85rem' }}>Kelas Asal (A)</h6></div>
                                <div className="card-body p-0 classes-list-container custom-scrollbar" style={{ maxHeight: '280px' }}>
                                    {mergeKyorugiClasses.map(c => (
                                        <div key={`A-${c.key}`} className={`p-2 p-md-3 border-bottom cursor-pointer transition-all hover-bg-light ${mergeClassA?.key === c.key ? 'bg-primary bg-opacity-10 border-start border-3 border-primary' : ''} ${c.athleteCount === 1 ? 'bg-danger bg-opacity-10' : ''}`} onClick={() => { setMergeClassA(c); setAthleteA(null); }}>
                                            <div className="d-flex justify-content-between align-items-center w-100 gap-1">
                                                <div className="text-truncate" style={{ fontSize: '0.6rem' }} title={`${c.weight_class} ${c.division} (${c.gender === 'Lelaki' ? 'Pa' : 'Pi'})`}>
                                                    <span className={`fw-black ${c.athleteCount === 1 ? 'text-danger' : 'text-dark'}`}>{c.weight_class}</span>
                                                    <span className="text-muted ms-1 d-none d-sm-inline">{c.division} ({c.gender === 'Lelaki' ? 'Pa' : 'Pi'})</span>
                                                    <span className="text-muted ms-1 d-inline d-sm-none">({c.gender === 'Lelaki' ? 'Pa' : 'Pi'})</span>
                                                </div>
                                                <div className={`badge ${c.athleteCount === 1 ? 'bg-danger shadow-sm' : 'bg-secondary'} flex-shrink-0`} style={{ fontSize: '0.6rem' }}>{c.athleteCount} Atl</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="card shadow-sm border-0 rounded-3 rounded-md-4 h-100 bg-white">
                                <div className="card-header bg-light p-2 p-md-3 border-bottom"><h6 className="m-0 fw-bold text-dark text-truncate" style={{ fontSize: '0.85rem' }}>Pilih Atlet (A)</h6></div>
                                <div className="card-body p-0 classes-list-container custom-scrollbar" style={{ maxHeight: '280px' }}>
                                    {!mergeClassA ? <div className="p-3 text-center text-muted mt-2" style={{ fontSize: '0.75rem' }}>Pilih kelas (kiri)</div> : athletesClassA.length === 0 ? <div className="p-3 text-center text-muted mt-2" style={{ fontSize: '0.75rem' }}>Kosong</div> : athletesClassA.map(a => (
                                        <div key={`athA-${a.uuid}`} className={`p-2 p-md-3 border-bottom cursor-pointer transition-all d-flex align-items-center gap-2 hover-bg-light ${athleteA?.uuid === a.uuid ? 'bg-primary bg-opacity-10 border-start border-3 border-primary' : ''}`} onClick={() => setAthleteA(a)}>
                                            <div className="rounded-circle bg-light border d-flex justify-content-center align-items-center text-muted flex-shrink-0" style={{ width: '24px', height: '24px' }}><FaUserNinja size={10} /></div>
                                            <div className="overflow-hidden">
                                                <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.6rem' }} title={getAthleteName(a)}>{getAthleteName(a)}</div>
                                                <div className="text-muted text-uppercase text-truncate" style={{ fontSize: '0.6rem' }} title={getTeamName(a)}>{getTeamName(a)}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex flex-row justify-content-center gap-2 gap-md-3 my-1 my-md-2 position-relative z-3">
                        <button className="btn btn-warning shadow-sm rounded-pill px-3 py-2 fw-bold text-dark d-flex align-items-center gap-1 hover-scale border" style={{ fontSize: '0.75rem' }} onClick={executeMoveToA}><FaArrowUp /> KE ATAS (A)</button>
                        <button className="btn btn-primary shadow-sm rounded-pill px-3 py-2 fw-bold d-flex align-items-center gap-1 hover-scale border" style={{ fontSize: '0.75rem' }} onClick={executeMoveToB}>KE BAWAH (B) <FaArrowDown /></button>
                    </div>

                    <div className="row g-2 g-md-3">
                        <div className="col-6">
                            <div className="card shadow-sm border-0 rounded-3 rounded-md-4 h-100 bg-white">
                                <div className="card-header bg-light p-2 p-md-3 border-bottom"><h6 className="m-0 fw-bold text-dark text-truncate" style={{ fontSize: '0.85rem' }}>Kelas Tujuan (B)</h6></div>
                                <div className="card-body p-0 classes-list-container custom-scrollbar" style={{ maxHeight: '280px' }}>
                                    {mergeKyorugiClasses.map(c => (
                                        <div key={`B-${c.key}`} className={`p-2 p-md-3 border-bottom cursor-pointer transition-all hover-bg-light ${mergeClassB?.key === c.key ? 'bg-primary bg-opacity-10 border-start border-3 border-primary' : ''} ${c.athleteCount === 1 ? 'bg-danger bg-opacity-10' : ''}`} onClick={() => { setMergeClassB(c); setAthleteB(null); }}>
                                            <div className="d-flex justify-content-between align-items-center w-100 gap-1">
                                                <div className="text-truncate" style={{ fontSize: '0.6rem' }} title={`${c.weight_class} ${c.division} (${c.gender === 'Lelaki' ? 'Pa' : 'Pi'})`}>
                                                    <span className={`fw-black ${c.athleteCount === 1 ? 'text-danger' : 'text-dark'}`}>{c.weight_class}</span>
                                                    <span className="text-muted ms-1 d-none d-sm-inline">{c.division} ({c.gender === 'Lelaki' ? 'Pa' : 'Pi'})</span>
                                                    <span className="text-muted ms-1 d-inline d-sm-none">({c.gender === 'Lelaki' ? 'Pa' : 'Pi'})</span>
                                                </div>
                                                <div className={`badge ${c.athleteCount === 1 ? 'bg-danger shadow-sm' : 'bg-secondary'} flex-shrink-0`} style={{ fontSize: '0.6rem' }}>{c.athleteCount} Atl</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="card shadow-sm border-0 rounded-3 rounded-md-4 h-100 bg-white">
                                <div className="card-header bg-light p-2 p-md-3 border-bottom"><h6 className="m-0 fw-bold text-dark text-truncate" style={{ fontSize: '0.85rem' }}>Pilih Atlet (B)</h6></div>
                                <div className="card-body p-0 classes-list-container custom-scrollbar" style={{ maxHeight: '280px' }}>
                                    {!mergeClassB ? <div className="p-3 text-center text-muted mt-2" style={{ fontSize: '0.75rem' }}>Pilih kelas (kiri)</div> : athletesClassB.length === 0 ? <div className="p-3 text-center text-muted mt-2" style={{ fontSize: '0.75rem' }}>Kosong</div> : athletesClassB.map(a => (
                                        <div key={`athB-${a.uuid}`} className={`p-2 p-md-3 border-bottom cursor-pointer transition-all d-flex align-items-center gap-2 hover-bg-light ${athleteB?.uuid === a.uuid ? 'bg-primary bg-opacity-10 border-start border-3 border-primary' : ''}`} onClick={() => setAthleteB(a)}>
                                            <div className="rounded-circle bg-light border d-flex justify-content-center align-items-center text-muted flex-shrink-0" style={{ width: '24px', height: '24px' }}><FaUserNinja size={10} /></div>
                                            <div className="overflow-hidden">
                                                <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.6rem' }} title={getAthleteName(a)}>{getAthleteName(a)}</div>
                                                <div className="text-muted text-uppercase text-truncate" style={{ fontSize: '0.6rem' }} title={getTeamName(a)}>{getTeamName(a)}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 1: GENERATE & SETTING */}
            {activeTab === 'generate' && (
                <div className="row g-3 pb-5">
                    <div className="col-lg-5">
                        <div className="card border-0 shadow-sm rounded-4 h-100 bg-white">
                            <div className="card-header bg-transparent p-3 border-bottom">
                                <h6 className="fw-bold text-dark m-0 small">Daftar Kelas (Belum & Sudah Terjadwal)</h6>
                            </div>
                            <div className="card-body p-0 custom-scrollbar classes-list-container" style={{ maxHeight: '700px' }}>
                                {loading && activeClassesGenerate.length === 0 ? <div className="text-center py-5"><div className="spinner-border text-primary"></div></div> : (
                                    <div className="list-group list-group-flush">
                                        {activeClassesGenerate.map((cls, idx) => {
                                            const isStaged = staging.find(s => s.key === cls.key);
                                            const dbLoc = classAllocationsDB[cls.key.toLowerCase()];
                                            let statusLabel = cls.competition_type === 'Poomsae' ? '✓ Siap (Acak)' : isStaged ? `⏳ Antre H${isStaged.targetDay}-Lap ${getArenaLetter(isStaged.targetArena)}` : dbLoc ? `✓ Terjadwal ${dbLoc}` : '⚪ Belum Alokasi';
                                            return (
                                                <div key={idx} className="p-2 border-bottom transition-all hover-bg-light">
                                                    <div className="d-flex justify-content-between align-items-center w-100 gap-1" style={{ fontSize: '0.6rem' }}>
                                                        <div className="text-truncate">
                                                            <span className="fw-black text-dark">{cls.weight_class}</span>
                                                            <span className="text-muted ms-1">{cls.division} ({cls.gender === 'Lelaki' ? 'Pa' : 'Pi'})</span>
                                                        </div>
                                                        <span className="badge bg-primary bg-opacity-10 text-primary fw-bold" style={{ fontSize: '0.55rem' }}>{cls.competition_type === 'Kyorugi' ? `${cls.athleteCount - 1} Partai` : `${cls.athleteCount} Atl`}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center mt-1">
                                                        <span className="text-muted fw-bold" style={{ fontSize: '0.55rem' }}>{statusLabel}</span>
                                                        <button className={`btn btn-sm ${dbLoc || isStaged ? 'btn-light border text-muted' : 'btn-primary'} rounded-pill px-3 py-1 fw-bold`} style={{ fontSize: '0.55rem' }} onClick={() => handlePindahKeKanan(cls)} disabled={!!dbLoc || !!isStaged || loading}>{dbLoc ? 'Selesai' : isStaged ? 'Antre' : 'Siapkan'}</button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-7 d-flex flex-column gap-3">
                        <div className="card border-0 shadow-sm rounded-4 bg-white">
                            <div className="card-header bg-transparent p-3 border-bottom"><h6 className="fw-bold text-dark m-0 small d-flex align-items-center"><FaMap className="text-primary me-2" /> Pilih Hari & Lapangan</h6></div>
                            <div className="card-body p-3">
                                <div className="row g-2">
                                    {[...Array(maxDays)].map((_, dIdx) => [...Array(arenasPerDay[dIdx] || 1)].map((_, aIdx) => {
                                        const day = dIdx + 1; const arena = aIdx + 1;
                                        const load = computedLoads[`${day}-${arena}`] || 0;
                                        const isTarget = genOptions.day === day && genOptions.arena === arena;
                                        return (
                                            <div className="col-4 col-md-3" key={`ha_${day}_${arena}`}>
                                                <button className={`btn w-100 p-2 border rounded-3 hover-scale ${isTarget ? 'btn-outline-primary border-2 bg-primary bg-opacity-10' : 'btn-white'}`} onClick={() => setGenOptions({ ...genOptions, day, arena })}>
                                                    <div className="fw-bold text-muted mb-1" style={{ fontSize: '0.65rem' }}>H{day}-Lap {getArenaLetter(arena)}</div>
                                                    <h6 className={`fw-black m-0 ${isTarget ? 'text-primary' : 'text-dark'}`}>{load} <span className="d-none d-md-inline fw-normal text-muted" style={{ fontSize: '0.55rem' }}>Partai</span></h6>
                                                </button>
                                            </div>
                                        );
                                    }))}
                                </div>
                            </div>
                        </div>
                        <div className="card border-0 shadow-sm rounded-4 bg-white">
                            <div className="card-header bg-warning bg-opacity-10 p-3 border-bottom"><h6 className="fw-bold text-dark m-0 small">Antrean Generate ({staging.length})</h6></div>
                            <div className="card-body p-2 custom-scrollbar" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                {staging.length === 0 ? <div className="text-center py-4 text-muted small fst-italic">Antrean kosong.</div> : staging.map(cls => (
                                    <div key={cls.key} className="border border-secondary border-opacity-25 rounded-3 p-2 mb-1 d-flex justify-content-between align-items-center bg-white shadow-sm hover-up">
                                        <div className="text-truncate pe-2">
                                            <div className="fw-black text-dark" style={{ fontSize: '0.7rem' }}>{cls.weight_class}</div>
                                            <div className="text-muted" style={{ fontSize: '0.6rem' }}>{cls.division} <span className="ms-1 fw-bold text-primary">➜ H{cls.targetDay}-Lap {getArenaLetter(cls.targetArena)}</span></div>
                                        </div>
                                        <button className="btn btn-sm btn-outline-danger border-0 rounded-circle" onClick={() => setStaging(staging.filter(s => s.key !== cls.key))}><FaTimes size={12} /></button>
                                    </div>
                                ))}
                            </div>
                            <div className="card-footer bg-white border-top p-2">
                                <button className="btn btn-primary w-100 py-2 rounded-pill fw-bold shadow-sm text-white fs-6 hover-scale" onClick={executeGenerateBatch} disabled={staging.length === 0}>CREATE BAGAN ({staging.length})</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 2: BAGAN (BRACKET) */}
            {activeTab === 'bracket' && (
                <div className="card border-0 shadow-sm rounded-4 flex-grow-1 animate-fade-in bg-white mb-4" style={{ minHeight: '60vh' }}>
                    <div className="card-header bg-transparent p-4 border-bottom">
                        <div className="row g-3 justify-content-between align-items-center">
                            <div className="col-12 col-xl-2"><h5 className="fw-bold m-0 text-dark">Bagan Prestasi</h5></div>
                            <div className="col-12 col-xl-10 d-flex flex-wrap align-items-center justify-content-xl-end gap-2">
                                <select className="form-select bg-primary bg-opacity-10 border-0 rounded-pill fw-bold px-3 py-2 w-auto" value={viewFilter.day} onChange={e => setViewFilter({ ...viewFilter, day: parseInt(e.target.value), weight_class: '' })}>
                                    {[...Array(maxDays)].map((_, i) => <option key={i} value={i + 1}>Hari {i + 1}</option>)}
                                </select>
                                <select className="form-select bg-primary bg-opacity-10 border-0 rounded-pill fw-bold px-3 py-2 w-auto" value={viewFilter.arena} onChange={e => setViewFilter({ ...viewFilter, arena: parseInt(e.target.value), weight_class: '' })}>
                                    {[...Array(arenasPerDay[viewFilter.day - 1] || 1)].map((_, i) => <option key={i} value={i + 1}>Lap {getArenaLetter(i + 1)}</option>)}
                                </select>
                                <select className="form-select bg-warning bg-opacity-10 border-0 rounded-pill fw-bold px-3 py-2 w-auto" style={{ minWidth: '250px' }} value={viewFilter.weight_class ? `${viewFilter.competition_type}|${viewFilter.gender}|${viewFilter.division}|${viewFilter.weight_class}` : ""} onChange={e => { if (!e.target.value) return; const [comp, gen, div, wc] = e.target.value.split('|'); setViewFilter({ ...viewFilter, competition_type: comp, gender: gen, division: div, weight_class: wc }); }}>
                                    {availableClassesForDropdown.length === 0 ? <option value="">-- Kosong --</option> : availableClassesForDropdown.map(c => <option key={`${c.competition_type}|${c.gender}|${c.division}|${c.weight_class}`} value={`${c.competition_type}|${c.gender}|${c.division}|${c.weight_class}`}>{`${c.division} ${c.weight_class} (${c.gender === 'Lelaki' ? 'Pa' : 'Pi'})`}</option>)}
                                </select>
                                <button className="btn btn-light rounded-circle border shadow-sm p-2 hover-scale" onClick={fetchBracketVisual}><FaUndo /></button>
                                <button className="btn btn-dark rounded-pill fw-bold shadow-sm d-flex align-items-center gap-2 px-3 py-2 ms-2 hover-scale" onClick={() => setPrintModal({ ...printModal, show: true, type: 'batch' })}><FaFilePdf /> Cetak Lapangan Ini</button>
                                <button className="btn btn-primary rounded-pill fw-bold shadow-sm d-flex align-items-center gap-2 px-3 py-2 hover-scale" onClick={() => setPrintModal({ ...printModal, show: true, type: 'single' })} disabled={!viewFilter.weight_class || bracketMatches.length === 0}><FaDownload /> Unduh (PDF)</button>
                            </div>
                        </div>
                    </div>
                    <div className="card-body p-0 bg-light overflow-auto bracket-scroll-container pb-5" style={{ minHeight: '400px' }}>
                        {loading ? <div className="text-center py-5"><div className="spinner-border text-primary"></div></div> : bracketMatches.length > 0 ? <BracketGridRenderer grouped={groupedMatches} keys={roundKeys} totalR={totalRounds} /> : <div className="text-center py-5 text-muted"><h5>Belum ada bagan.</h5></div>}
                    </div>
                </div>
            )}

            {/* TAB 3: SCHEDULE */}
            {activeTab === 'schedule' && (
                <div className="card border-0 shadow-sm rounded-4 flex-grow-1 animate-fade-in bg-white mb-4">
                    <div className="card-header bg-transparent p-4 border-bottom">
                        <div className="row justify-content-between align-items-center g-3">
                            <div className="col-12 col-md-auto"><h5 className="fw-bold m-0 text-dark d-flex align-items-center gap-2"><FaCalendarAlt className="text-primary" /> Jadwal</h5></div>
                            <div className="col-12 col-md-auto">
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    <select className="form-select bg-light border-0 rounded-pill fw-bold px-3 py-2 w-auto" value={schedFilter.day} onChange={e => setSchedFilter({ ...schedFilter, day: parseInt(e.target.value) })}>{[...Array(maxDays)].map((_, i) => <option key={i} value={i + 1}>Hari ke-{i + 1}</option>)}</select>
                                    <select className="form-select bg-light border-0 rounded-pill fw-bold px-3 py-2 w-auto" value={schedFilter.arena} onChange={e => setSchedFilter({ ...schedFilter, arena: parseInt(e.target.value) })}>{[...Array(arenasPerDay[schedFilter.day - 1] || 1)].map((_, i) => <option key={i} value={i + 1}>Lap {getArenaLetter(i + 1)}</option>)}</select>
                                    <button className="btn btn-dark rounded-pill fw-bold shadow-sm d-flex align-items-center gap-2 px-3 py-2 hover-scale" onClick={() => setPrintModal({ ...printModal, show: true, type: 'schedule' })}><FaDownload /> PDF</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="card-body p-4 bg-light rounded-bottom-4" style={{ backgroundColor: '#f8f9fa' }}>
                        {loading ? <div className="text-center py-5"><div className="spinner-border text-primary"></div></div> : scheduleMatchesToRender.length === 0 ? <div className="text-center py-5 text-muted">Kosong.</div> : (
                            <div className="row g-4">
                                {scheduleMatchesToRender.map((m) => (
                                    <div className="col-md-6 col-lg-4" key={m.id}>
                                        <div className="card h-100 shadow-sm border-0 rounded-4 overflow-hidden match-card">
                                            <div className="bg-white p-3 border-bottom d-flex justify-content-between align-items-center">
                                                <span className="badge bg-light text-dark border px-3 py-2 fw-bold" style={{ fontSize: '0.8rem' }}>#{m.match_num || m.match_no}</span>
                                                <div className="text-end"><div className="fw-bold text-dark" style={{ fontSize: '0.8rem' }}>{m.division} - {m.weight_class}</div></div>
                                            </div>
                                            <div className="card-body p-0 d-flex flex-column bg-white">
                                                {renderScheduleParticipant(m, 'blue')}
                                                {renderScheduleParticipant(m, 'red')}
                                            </div>
                                            <div className="card-footer bg-white border-top p-2 no-print d-flex gap-2" data-html2canvas-ignore="true">
                                                <button className="btn btn-sm btn-light flex-fill text-muted small d-flex justify-content-center align-items-center gap-2 hover-bg-light fw-bold" onClick={() => handleCallAthlete(m)}>
                                                    <FaBullhorn className="text-primary" /> Panggil
                                                </button>
                                                <button className="btn btn-sm btn-light flex-fill text-muted small d-flex justify-content-center align-items-center gap-2 hover-bg-light fw-bold" onClick={() => executeTASheetExport(m)}>
                                                    <FaFilePdf className="text-danger" /> TA Sheet
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* MODAL INJECT ATHLETE KE BYE */}
            {injectModal.show && createPortal(
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1200, background: 'rgba(0,0,0,0.6)' }}>
                    <div className="card border-0 shadow-lg animate-pop-in" style={{ width: '90%', maxWidth: '450px', borderRadius: '20px' }}>
                        <div className="card-header bg-white p-4 border-bottom-0 d-flex justify-content-between align-items-center">
                            <h5 className="fw-black text-dark m-0 d-flex align-items-center gap-2"><FaCogs className="text-primary" /> Sisipkan Atlet (Edit BYE)</h5>
                            <button onClick={() => setInjectModal({ show: false, match: null, athleteId: '', matchNo: '', sequence: '' })} className="btn btn-sm btn-light rounded-circle text-danger"><FaTimes /></button>
                        </div>
                        <div className="card-body px-4 pb-4 pt-2">
                            <div className="alert alert-info border-0 rounded-3 small py-2 px-3 mb-4">Ubah partai BYE jadi aktif. Sisipkan atlet yg belum ada bagan.</div>
                            <div className="mb-3">
                                <label className="form-label small fw-bold text-dark">Pilih Atlet</label>
                                <select className="form-select bg-light border-0 py-2 px-3 rounded-3 fw-bold" value={injectModal.athleteId} onChange={e => setInjectModal({ ...injectModal, athleteId: e.target.value })}>
                                    <option value="">-- Pilih Atlet --</option>
                                    {availableAthletesForInject.map(a => <option key={a.id} value={a.id}>{getAthleteName(a)} ({getTeamName(a)})</option>)}
                                </select>
                            </div>
                            <div className="row g-2 mb-4">
                                <div className="col-6">
                                    <label className="form-label small fw-bold text-dark">No Tampil</label>
                                    <input type="text" className="form-control bg-light border-0 py-2 px-3 fw-bold" value={injectModal.matchNo} onChange={e => setInjectModal({ ...injectModal, matchNo: e.target.value })} />
                                </div>
                                <div className="col-6">
                                    <label className="form-label small fw-bold text-dark">Urutan Seq</label>
                                    <input type="number" className="form-control bg-light border-0 py-2 px-3 fw-bold" value={injectModal.sequence} onChange={e => setInjectModal({ ...injectModal, sequence: e.target.value })} />
                                </div>
                            </div>
                            <div className="d-flex gap-2 justify-content-end">
                                <button className="btn btn-light rounded-pill px-4 fw-bold" onClick={() => setInjectModal({ show: false, match: null, athleteId: '', matchNo: '', sequence: '' })}>Batal</button>
                                <button className="btn btn-primary rounded-pill px-4 fw-bold" onClick={handleInjectSubmit} disabled={!injectModal.athleteId}>Simpan</button>
                            </div>
                        </div>
                    </div>
                </div>, document.body
            )}

            {/* MODAL EDIT PARTAI */}
            {editMatchModal.show && createPortal(
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1200, background: 'rgba(0,0,0,0.6)' }}>
                    <div className="card border-0 shadow-lg animate-pop-in" style={{ width: '92%', maxWidth: '560px', borderRadius: '20px' }}>
                        <div className="card-header bg-white p-4 border-bottom-0 d-flex justify-content-between align-items-center">
                            <h5 className="fw-black text-dark m-0 d-flex align-items-center gap-2"><FaCogs className="text-primary" /> Edit Partai Prestasi</h5>
                            <button onClick={() => setEditMatchModal({ show: false, match: null, matchNum: '', sequence: '', blueId: '', redId: '' })} className="btn btn-sm btn-light rounded-circle text-danger"><FaTimes /></button>
                        </div>
                        <div className="card-body px-4 pb-4 pt-2">
                            <div className="row g-3 mb-3">
                                <div className="col-md-7">
                                    <label className="form-label small fw-bold text-dark">Nama / Nomor Partai</label>
                                    <input type="text" className="form-control bg-light border-0 py-2 px-3 rounded-3 fw-bold" value={editMatchModal.matchNum} onChange={e => setEditMatchModal({ ...editMatchModal, matchNum: e.target.value })} />
                                </div>
                                <div className="col-md-5">
                                    <label className="form-label small fw-bold text-dark">Urutan Jadwal</label>
                                    <input type="number" className="form-control bg-light border-0 py-2 px-3 rounded-3 fw-bold" value={editMatchModal.sequence} onChange={e => setEditMatchModal({ ...editMatchModal, sequence: e.target.value })} />
                                </div>
                            </div>
                            <div className="row g-3 mb-4">
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold text-primary">Sudut Biru</label>
                                    <select className="form-select bg-light border-0 py-2 px-3 rounded-3 fw-bold" value={editMatchModal.blueId} onChange={e => setEditMatchModal({ ...editMatchModal, blueId: e.target.value })}>
                                        <option value="">-- Kosong --</option>
                                        {editableAthleteOptions.map(a => <option key={a.id} value={a.id}>{getAthleteName(a)} ({getTeamName(a)})</option>)}
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold text-danger">Sudut Merah</label>
                                    <select className="form-select bg-light border-0 py-2 px-3 rounded-3 fw-bold" value={editMatchModal.redId} onChange={e => setEditMatchModal({ ...editMatchModal, redId: e.target.value })}>
                                        <option value="">-- Kosong --</option>
                                        {editableAthleteOptions.map(a => <option key={a.id} value={a.id}>{getAthleteName(a)} ({getTeamName(a)})</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="alert alert-warning border-0 rounded-3 small py-2 px-3 mb-4">
                                Mengubah peserta akan mereset pemenang partai ini dan menyesuaikan slot babak berikutnya.
                            </div>
                            <div className="d-flex gap-2 justify-content-end">
                                <button className="btn btn-light rounded-pill px-4 fw-bold" onClick={() => setEditMatchModal({ show: false, match: null, matchNum: '', sequence: '', blueId: '', redId: '' })}>Batal</button>
                                <button className="btn btn-primary rounded-pill px-4 fw-bold shadow-sm" onClick={handleEditMatchSubmit} disabled={loading}>Simpan Perubahan</button>
                            </div>
                        </div>
                    </div>
                </div>, document.body
            )}

            {/* MODAL PRINT PDF INPUT EVENT NAME */}
            {printModal.show && createPortal(
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1200, background: 'rgba(0,0,0,0.6)' }}>
                    <div className="card border-0 shadow-lg animate-pop-in" style={{ width: '90%', maxWidth: '450px', borderRadius: '20px' }}>
                        <div className="card-header bg-white p-4 border-bottom-0 d-flex justify-content-between align-items-center">
                            <h5 className="fw-black text-dark m-0 d-flex align-items-center gap-2"><FaFilePdf className="text-danger" /> Unduh Dokumen</h5>
                            <button onClick={() => setPrintModal({ ...printModal, show: false })} className="btn btn-sm btn-light rounded-circle text-danger"><FaTimes /></button>
                        </div>
                        <div className="card-body px-4 pb-4 pt-2">
                            <p className="text-muted small mb-3">Sesuaikan nama event yang akan dicetak pada dokumen.</p>
                            <div className="mb-4">
                                <label className="form-label small fw-bold text-dark">Nama Event Kejuaraan</label>
                                <input type="text" className="form-control bg-light border-0 py-3 px-4 rounded-pill fw-bold" value={printModal.eventName} onChange={(e) => setPrintModal({ ...printModal, eventName: e.target.value })} placeholder="Contoh: KEJUARAAN BUPATI CUP" />
                            </div>
                            <div className="d-flex gap-2 justify-content-end">
                                <button className="btn btn-light rounded-pill px-4 fw-bold" onClick={() => setPrintModal({ ...printModal, show: false })}>Batal</button>
                                <button className="btn btn-primary rounded-pill px-4 fw-bold shadow-sm d-flex align-items-center gap-2" onClick={executePDFExport}>
                                    <FaDownload /> Render & Simpan
                                </button>
                            </div>
                        </div>
                    </div>
                </div>, document.body
            )}

            {/* MODAL CONFIRMATION */}
            {confirmModal.show && createPortal(
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1200, background: 'rgba(0,0,0,0.5)' }}>
                    <div className="card border-0 shadow-lg animate-pop-in" style={{ width: '90%', maxWidth: '400px', borderRadius: '24px' }}>
                        <div className="card-body p-5 text-center">
                            <div className="mb-3"><FaQuestionCircle size={55} className="text-warning opacity-75" /></div>
                            <h5 className="fw-bold text-dark mb-2">{confirmModal.title}</h5>
                            <p className="text-muted small mb-4">{confirmModal.message}</p>
                            <div className="d-flex gap-2 justify-content-center">
                                <button className="btn btn-light border rounded-pill px-4 fw-bold" onClick={() => setConfirmModal({ show: false, title: '', message: '', action: null })}>Batal</button>
                                <button className="btn btn-primary rounded-pill px-4 fw-bold shadow-sm" onClick={confirmModal.action}>Ya, Lanjutkan</button>
                            </div>
                        </div>
                    </div>
                </div>, document.body
            )}

            {/* MODAL CHAMPION CELEBRATION */}
            {showChampionModal && champion && createPortal(
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1100, background: 'rgba(0,0,0,0.7)' }}>
                    <div className="card border-0 shadow-lg text-center overflow-hidden animate-pop-in" style={{ maxWidth: '400px', width: '90%', borderRadius: '24px' }}>
                        <div className="bg-warning p-4 position-relative">
                            <button className="btn btn-sm btn-link text-dark position-absolute top-0 end-0 m-2" onClick={() => setShowChampionModal(false)}><FaTimes size={20} /></button>
                            <FaCrown size={60} className="text-white mb-2" style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.2))' }} />
                            <h2 className="fw-black text-dark m-0">JUARA 1</h2>
                        </div>
                        <div className="p-5 bg-white">
                            <h3 className="fw-bold text-primary mb-1">{getAthleteName(champion)}</h3>
                            <p className="text-muted fw-bold mb-4 text-uppercase">{getTeamName(champion)}</p>
                            <FaMedal size={50} className="text-warning mb-3" />
                            <p className="small text-muted m-0">Pertandingan kelas {viewFilter.weight_class} telah selesai.</p>
                        </div>
                    </div>
                </div>, document.body
            )}

            {/* CSS Global Internal */}
            <style>{`
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                .animate-pop-in { animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
                .transition-all { transition: all 0.2s ease-in-out; }
                .hover-scale { transition: transform 0.2s; }
                .hover-scale:hover { transform: scale(1.05); }
                .hover-up:hover { transform: translateY(-4px); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1) !important; }
                .hover-bg-light:hover { background-color: #f8f9fa !important; }
                .cursor-pointer { cursor: pointer; }
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #dee2e6; border-radius: 10px; }
                .classes-list-container { max-height: 400px; overflow-y: auto; }
                @media (min-width: 992px) { .classes-list-container { max-height: 750px; } }
                .bracket-scroll-container { position: relative; width: 100%; cursor: grab; }
                .bracket-wrapper { display: flex; flex-direction: row; align-items: stretch; margin-top: 20px; padding-bottom: 50px; }
                .connector-line-right { position: absolute; right: -25px; top: 50%; width: 25px; height: 2px; background-color: #cbd5e1; z-index: 1; }
                .connector-line-left { position: absolute; left: -25px; top: 50%; width: 25px; height: 2px; background-color: #cbd5e1; z-index: 1; }
                .match-card-wrapper { display: flex; flex-direction: column; justify-content: center; z-index: 2; page-break-inside: avoid; }
                .match-card { min-width: 240px; transition: transform 0.2s, box-shadow 0.2s; }
                .match-edit-label { font-size: 0.6rem; line-height: 1; }
                .prestasi-participant-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    background: #ffffff;
                    min-height: 88px;
                }
                .prestasi-participant-info {
                    min-width: 0;
                    flex: 1 1 auto;
                    overflow: hidden;
                }
                .prestasi-action-area {
                    flex: 0 0 auto;
                    display: flex;
                    justify-content: flex-end;
                    align-items: center;
                    min-width: 96px;
                }
                .prestasi-set-btn {
                    white-space: nowrap;
                    min-width: 96px;
                    font-size: 0.75rem;
                    padding: 0.4rem 0.75rem;
                }
                @media (max-width: 420px) {
                    .prestasi-participant-row {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .prestasi-action-area {
                        width: 100%;
                        justify-content: flex-start;
                    }
                }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes popIn { 0% { transform: scale(0.9); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
            `}</style>

            {/* MODAL DETAIL ATLET */}
            {selectedAthlete && createPortal(
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 2000, background: 'rgba(0,0,0,0.6)' }}>
                    <div className="card border-0 shadow-lg animate-pop-in" style={{ width: '90%', maxWidth: '350px', borderRadius: '20px' }}>
                        <div className="card-body p-4 text-center">
                            <div className="rounded-circle bg-primary bg-opacity-10 mx-auto mb-3 d-flex justify-content-center align-items-center" style={{ width: '60px', height: '60px' }}>
                                <FaUserNinja size={30} className="text-primary" />
                            </div>
                            <h6 className="fw-black mb-1">{getAthleteName(selectedAthlete)}</h6>
                            <p className="text-muted small mb-3">{getTeamName(selectedAthlete)}</p>
                            <div className="row g-2">
                                <div className="col-4">
                                    <div className="bg-light p-2 rounded-3">
                                        <small className="d-block text-muted">Umur</small>
                                        <strong className="text-dark">{selectedAthlete.age || '-'}</strong>
                                    </div>
                                </div>
                                <div className="col-4">
                                    <div className="bg-light p-2 rounded-3">
                                        <small className="d-block text-muted">Berat</small>
                                        <strong className="text-dark">{selectedAthlete.weight || '-'}</strong>
                                    </div>
                                </div>
                                <div className="col-4">
                                    <div className="bg-light p-2 rounded-3">
                                        <small className="d-block text-muted">Tinggi</small>
                                        <strong className="text-dark">{selectedAthlete.height || '-'}</strong>
                                    </div>
                                </div>
                            </div>
                            <button className="btn btn-primary w-100 rounded-pill mt-4 fw-bold" onClick={() => setSelectedAthlete(null)}>Tutup</button>
                        </div>
                    </div>
                </div>, document.body
            )}
        </div >
    );
};

export default AdminKyorugiPrestasi;
